/**
 * PÁGINA DE CALENDARIO DE REMATES
 * ================================
 * Muestra el calendario anual de remates con colores por estado
 */

import React, { useState, useEffect } from 'react';
import { Calendar, ChevronLeft, ChevronRight, MapPin, Clock, Loader2, Users } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import ModernHeader from '../components/modern/ModernHeader';
import ModernFooter from '../components/modern/ModernFooter';
import { api } from '../services/api';

// Mapeo de estados a colores
const ESTADO_COLORS = {
  1: { bg: 'bg-amber-500', light: 'bg-amber-50', border: 'border-amber-300', text: 'text-amber-700', label: 'Agendado' },
  2: { bg: 'bg-green-500', light: 'bg-green-50', border: 'border-green-300', text: 'text-green-700', label: 'Activo' },
  3: { bg: 'bg-red-500', light: 'bg-red-50', border: 'border-red-300', text: 'text-red-700', label: 'En Curso' },
  4: { bg: 'bg-gray-400', light: 'bg-gray-50', border: 'border-gray-300', text: 'text-gray-600', label: 'Finalizado' }
};

const CalendarioPage = () => {
  const navigate = useNavigate();
  const [scrollY, setScrollY] = useState(0);
  const [vistaActual, setVistaActual] = useState('mes');
  const [mesSeleccionado, setMesSeleccionado] = useState(new Date().getMonth());
  const [añoSeleccionado, setAñoSeleccionado] = useState(new Date().getFullYear());
  const [remates, setRemates] = useState([]);
  const [loading, setLoading] = useState(true);

  // Manejar scroll para header
  useEffect(() => {
    const handleScroll = () => setScrollY(window.scrollY);
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Cargar remates del año
  useEffect(() => {
    const fetchRemates = async () => {
      try {
        setLoading(true);
        const data = await api.getRematesCalendario(añoSeleccionado);
        console.log('[CalendarioPage] Remates cargados:', data);
        setRemates(data);
      } catch (error) {
        console.error('[CalendarioPage] Error cargando remates:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchRemates();
  }, [añoSeleccionado]);

  const meses = [
    'Enero', 'Febrero', 'Marzo', 'Abril', 'Mayo', 'Junio',
    'Julio', 'Agosto', 'Septiembre', 'Octubre', 'Noviembre', 'Diciembre'
  ];

  const diasSemana = ['Dom', 'Lun', 'Mar', 'Mié', 'Jue', 'Vie', 'Sáb'];

  // Obtener días del mes
  const obtenerDiasDelMes = (mes, año) => {
    const primerDia = new Date(año, mes, 1);
    const ultimoDia = new Date(año, mes + 1, 0);
    const diasEnMes = ultimoDia.getDate();
    const primerDiaSemana = primerDia.getDay();

    const dias = [];
    for (let i = 0; i < primerDiaSemana; i++) {
      dias.push(null);
    }
    for (let i = 1; i <= diasEnMes; i++) {
      dias.push(i);
    }
    return dias;
  };

  // Obtener remate en una fecha específica
  const obtenerRemateEnFecha = (dia, mes, año) => {
    if (!dia) return null;
    const fechaBuscada = `${año}-${String(mes + 1).padStart(2, '0')}-${String(dia).padStart(2, '0')}`;
    return remates.find(remate => remate.fechas.includes(fechaBuscada));
  };

  // Navegación de mes
  const mesAnterior = () => {
    if (mesSeleccionado === 0) {
      setMesSeleccionado(11);
      setAñoSeleccionado(añoSeleccionado - 1);
    } else {
      setMesSeleccionado(mesSeleccionado - 1);
    }
  };

  const mesSiguiente = () => {
    if (mesSeleccionado === 11) {
      setMesSeleccionado(0);
      setAñoSeleccionado(añoSeleccionado + 1);
    } else {
      setMesSeleccionado(mesSeleccionado + 1);
    }
  };

  // Filtrar próximos remates
  const hoy = new Date();
  const eventosProximos = remates
    .filter(remate => {
      const fechaMasTarde = remate.fechas[remate.fechas.length - 1];
      return new Date(fechaMasTarde) >= hoy;
    })
    .sort((a, b) => new Date(a.fechas[0]) - new Date(b.fechas[0]))
    .slice(0, 5);

  // Renderizar vista por mes
  const renderVistaMes = () => {
    const dias = obtenerDiasDelMes(mesSeleccionado, añoSeleccionado);

    return (
      <div className="bg-white rounded-2xl shadow-xl p-6 border border-gray-100">
        {/* Header del calendario */}
        <div className="flex items-center justify-between mb-6 pb-4 border-b border-gray-200">
          <button
            onClick={mesAnterior}
            className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
          >
            <ChevronLeft className="w-6 h-6 text-gray-600" />
          </button>

          <h2 className="text-2xl font-bold text-gray-900">
            {meses[mesSeleccionado]} {añoSeleccionado}
          </h2>

          <button
            onClick={mesSiguiente}
            className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
          >
            <ChevronRight className="w-6 h-6 text-gray-600" />
          </button>
        </div>

        {/* Días de la semana */}
        <div className="grid grid-cols-7 gap-2 mb-2">
          {diasSemana.map(dia => (
            <div key={dia} className="text-center py-2 text-sm font-semibold text-gray-600">
              {dia}
            </div>
          ))}
        </div>

        {/* Días del mes */}
        <div className="grid grid-cols-7 gap-2">
          {dias.map((dia, index) => {
            const remate = obtenerRemateEnFecha(dia, mesSeleccionado, añoSeleccionado);
            const esHoy = dia === new Date().getDate() &&
              mesSeleccionado === new Date().getMonth() &&
              añoSeleccionado === new Date().getFullYear();
            const estadoColor = remate ? ESTADO_COLORS[remate.estado] || ESTADO_COLORS[4] : null;

            return (
              <div
                key={index}
                onClick={() => remate && navigate(`/lotes?remate=${remate.numero}`)}
                className={`
                  min-h-[90px] p-2 rounded-lg border transition-all duration-200
                  ${dia ? 'bg-white' : 'bg-transparent border-transparent'}
                  ${esHoy ? 'ring-2 ring-green-500' : 'border-gray-200'}
                  ${remate ? `${estadoColor.light} ${estadoColor.border} hover:shadow-md cursor-pointer` : ''}
                `}
              >
                {dia && (
                  <>
                    <div className={`text-sm font-semibold mb-1 ${esHoy ? 'text-green-600' : 'text-gray-900'}`}>
                      {dia}
                    </div>
                    {remate && (
                      <div className="text-xs space-y-1">
                        <div className={`${estadoColor.bg} text-white px-2 py-1 rounded font-bold`}>
                          R{remate.numero}
                        </div>
                        {remate.lugar && (
                          <div className="text-gray-600 truncate text-[10px]">
                            {remate.lugar.split('-')[0]}
                          </div>
                        )}
                      </div>
                    )}
                  </>
                )}
              </div>
            );
          })}
        </div>

        {/* Leyenda */}
        <div className="mt-6 pt-4 border-t border-gray-200">
          <div className="flex flex-wrap gap-4 justify-center">
            {Object.entries(ESTADO_COLORS).map(([estado, colors]) => (
              <div key={estado} className="flex items-center gap-2">
                <div className={`w-3 h-3 rounded-full ${colors.bg}`}></div>
                <span className="text-xs text-gray-600 font-medium">{colors.label}</span>
              </div>
            ))}
          </div>
        </div>
      </div>
    );
  };

  // Renderizar vista por año
  const renderVistaAño = () => {
    return (
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {meses.map((mes, mesIndex) => {
          const dias = obtenerDiasDelMes(mesIndex, añoSeleccionado);
          const eventosEnMes = remates.filter(remate =>
            remate.fechas.some(fecha => {
              const [year, month] = fecha.split('-');
              return parseInt(month) === mesIndex + 1 && parseInt(year) === añoSeleccionado;
            })
          );

          return (
            <div
              key={mes}
              className="bg-white rounded-xl shadow-lg p-4 border border-gray-100 hover:shadow-xl transition-shadow cursor-pointer"
              onClick={() => {
                setVistaActual('mes');
                setMesSeleccionado(mesIndex);
              }}
            >
              <h3 className="text-lg font-bold text-gray-900 mb-3 flex items-center justify-between">
                {mes}
                {eventosEnMes.length > 0 && (
                  <span className="bg-green-100 text-green-700 text-xs font-bold px-2 py-1 rounded-full">
                    {eventosEnMes.length}
                  </span>
                )}
              </h3>

              <div className="grid grid-cols-7 gap-1">
                {dias.map((dia, index) => {
                  const remate = obtenerRemateEnFecha(dia, mesIndex, añoSeleccionado);
                  const estadoColor = remate ? ESTADO_COLORS[remate.estado] || ESTADO_COLORS[4] : null;

                  return (
                    <div
                      key={index}
                      className={`
                        text-center text-xs py-1 rounded
                        ${dia ? 'text-gray-700' : 'text-transparent'}
                        ${remate ? `${estadoColor.bg} text-white font-bold` : ''}
                      `}
                    >
                      {dia || '-'}
                    </div>
                  );
                })}
              </div>
            </div>
          );
        })}
      </div>
    );
  };

  // Formatear fecha
  const formatFecha = (fechaISO) => {
    const date = new Date(fechaISO + 'T12:00:00');
    return date.toLocaleDateString('es-UY', { day: 'numeric', month: 'short' });
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 via-white to-green-50">
      <ModernHeader scrollY={100} />

      <div className="pt-24 pb-12">
        <div className="max-w-7xl mx-auto px-4">

          {/* Header de la página */}
          <div className="mb-8">
            <div className="flex flex-col md:flex-row items-start md:items-center justify-between gap-4">
              <div>
                <h1 className="text-4xl font-bold text-gray-900 mb-2 flex items-center gap-3">
                  <Calendar className="w-10 h-10 text-green-600" />
                  Calendario de Remates
                </h1>
                <p className="text-gray-600 text-lg">
                  Planifica tu año con todas las fechas de nuestros remates
                </p>
              </div>

              {/* Toggle de vista */}
              <div className="flex gap-2 bg-white rounded-xl p-1 shadow-md border border-gray-200">
                <button
                  onClick={() => setVistaActual('mes')}
                  className={`px-6 py-2 rounded-lg font-semibold transition-all duration-300 ${vistaActual === 'mes'
                      ? 'bg-gradient-to-r from-green-600 to-green-500 text-white shadow-lg'
                      : 'text-gray-600 hover:bg-gray-50'
                    }`}
                >
                  Por Mes
                </button>
                <button
                  onClick={() => setVistaActual('año')}
                  className={`px-6 py-2 rounded-lg font-semibold transition-all duration-300 ${vistaActual === 'año'
                      ? 'bg-gradient-to-r from-green-600 to-green-500 text-white shadow-lg'
                      : 'text-gray-600 hover:bg-gray-50'
                    }`}
                >
                  Por Año
                </button>
              </div>
            </div>
          </div>

          {/* Loading state */}
          {loading && (
            <div className="flex items-center justify-center py-16">
              <div className="text-center">
                <Loader2 className="w-12 h-12 text-green-600 animate-spin mx-auto mb-4" />
                <p className="text-gray-600 font-medium">Cargando calendario...</p>
              </div>
            </div>
          )}

          {!loading && (
            <div className="grid lg:grid-cols-3 gap-6">

              {/* Calendario principal */}
              <div className="lg:col-span-2">
                {vistaActual === 'mes' ? renderVistaMes() : renderVistaAño()}
              </div>

              {/* Sidebar con próximos eventos */}
              <div className="lg:col-span-1">
                <div className="bg-white rounded-2xl shadow-xl p-6 border border-gray-100 sticky top-24">
                  <h3 className="text-xl font-bold text-gray-900 mb-4 flex items-center gap-2">
                    <Clock className="w-5 h-5 text-green-600" />
                    Próximos Remates
                  </h3>

                  {eventosProximos.length === 0 ? (
                    <p className="text-gray-500 text-center py-4">No hay remates próximos</p>
                  ) : (
                    <div className="space-y-4">
                      {eventosProximos.map(remate => {
                        const estadoColor = ESTADO_COLORS[remate.estado] || ESTADO_COLORS[4];
                        const fechaStr = remate.fechas.length === 1
                          ? formatFecha(remate.fechas[0])
                          : `${formatFecha(remate.fechas[0])} - ${formatFecha(remate.fechas[remate.fechas.length - 1])}`;

                        return (
                          <div
                            key={remate.id}
                            onClick={() => navigate(`/lotes?remate=${remate.numero}`)}
                            className={`p-4 rounded-xl ${estadoColor.light} border ${estadoColor.border} hover:shadow-lg transition-all cursor-pointer`}
                          >
                            <div className="flex items-start justify-between mb-2">
                              <span className={`${estadoColor.bg} text-white px-3 py-1 rounded-full text-sm font-bold`}>
                                R{remate.numero}
                              </span>
                              <span className="text-xs text-gray-600 font-semibold">
                                {fechaStr}
                              </span>
                            </div>

                            <div className="space-y-1 text-sm text-gray-600">
                              {remate.lugar && (
                                <div className="flex items-center gap-2">
                                  <MapPin className="w-4 h-4 text-green-600 flex-shrink-0" />
                                  <span className="truncate">{remate.lugar}</span>
                                </div>
                              )}
                              <div className="flex items-center gap-2">
                                <Users className="w-4 h-4 text-green-600 flex-shrink-0" />
                                {remate.total_lotes} lotes
                              </div>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  )}
                </div>
              </div>

            </div>
          )}
        </div>
      </div>

      <ModernFooter />
    </div>
  );
};

export default CalendarioPage;
